import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchTransactionTypesList } from './useFetchTransactionTypesList';
import { useFetchMatterById } from './useFetchMatterById';
import { useHasTransactionTypeCategories } from './useHasTransactionTypeCategories';
import { useTransaction } from '../context/TransactionContext';
import { useTimer } from '../context/TimerContext';
import { useLanguage } from '../context/LanguageContext';
import { IFilterOption } from '../interface/transaction';
import {
  IMatterTransactionTypesResponse,
  ITransactionTypeFilterSelection,
  ITransactionTypes,
} from '../interface/transactionTypes';
import { TransactionTypesValue } from '../constants/transactionTypes';
import { getTranslationFromTranslatableEntity, InvoiceableType } from '../constants/matter';
import { SystemConstants } from '../constants/constants';
import { ISortOption, IActiveSortState } from '../interface/common';
import {
  buildCategoryFilterOptions,
  filterTransactionTypesByCategory,
  groupTransactionTypesByCategory,
  ITransactionTypeGroup,
  NO_CATEGORY_GROUP_ID,
} from '../components/dashboard/addTransaction/groupTransactionTypesByCategory';

type TranslateFn = (key: string) => string;

const getTransactionTypesFilterOptions = (
  t: TranslateFn,
  hasLinkedTransactionTypes: boolean | undefined,
): IFilterOption[] => [
  {
    label: t('transaction:transactionType.filter.billable'),
    value: TransactionTypesValue.billableMatter,
  },
  {
    label: t('transaction:transactionType.filter.internal'),
    value: TransactionTypesValue.internalMatter,
  },
  ...(hasLinkedTransactionTypes
    ? [
        {
          label: t('transaction:transactionType.filter.matterTransactionTypes'),
          value: TransactionTypesValue.matterTransactionTypes,
        },
      ]
    : []),
  { label: t('transaction:transactionType.filter.all'), value: TransactionTypesValue.all },
];

// Whenever the matter has linked transaction types, only the matterTransactionTypes filter and
// the matter's own invoiceable-type filter are valid picks — regardless of which of the two is
// currently active — the other two stay visible but non-selectable.
const getDisabledTransactionTypesFilterValues = (
  hasLinkedTransactionTypes: boolean | undefined,
  matterInvoiceableTypeFilter: IFilterOption,
): string[] => {
  if (!hasLinkedTransactionTypes) {
    return [];
  }
  return [
    TransactionTypesValue.billableMatter,
    TransactionTypesValue.internalMatter,
    TransactionTypesValue.all,
  ].filter((value) => value !== matterInvoiceableTypeFilter.value);
};

const MATTER_TYPES_PINNING_THE_FILTER = new Set<string | undefined>([
  InvoiceableType.billable,
  InvoiceableType.internal,
  InvoiceableType.nonBillable,
]);
const INVOICEABLE_TYPE_FILTER_VALUES = new Set<string | undefined>([
  TransactionTypesValue.billableMatter,
  TransactionTypesValue.internalMatter,
]);

// A matter with a fixed invoiceable type pins the filter to it. When the matter has linked
// transaction types that default no longer applies, so the filter only locks once the user has
// swapped to one of the invoiceable-type filters.
const isTransactionTypesFilterLocked = (
  hasLinkedTransactionTypes: boolean | undefined,
  matterType: string | undefined,
  selectedFilterValue: string | undefined,
): boolean =>
  hasLinkedTransactionTypes
    ? INVOICEABLE_TYPE_FILTER_VALUES.has(selectedFilterValue)
    : MATTER_TYPES_PINNING_THE_FILTER.has(matterType);

const NO_CATEGORY_FILTERS: IFilterOption[] = [];

const getSelectionForMatter = (
  selection: ITransactionTypeFilterSelection | null | undefined,
  matterId: string | undefined,
) => (selection && selection.matterId === matterId ? selection : null);

interface UseTransactionTypeListOptions {
  onlyHourBased?: boolean;
  /** Which form's filter choice to share; the picker page reads it from the route it was opened with. */
  isFromTimer?: boolean;
}

export const useTransactionTypeList = ({
  onlyHourBased,
  isFromTimer,
}: UseTransactionTypeListOptions = {}) => {
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const appLanguage = getAppLanguageCode();
  const queryClient = useQueryClient();
  const location = useLocation();
  const {
    transaction,
    transactionTypeFilterSelection: transactionFormSelection,
    updateTransactionTypeFilterSelection: updateTransactionFormSelection,
  } = useTransaction();
  const {
    timer,
    transactionTypeFilterSelection: timerFormSelection,
    updateTransactionTypeFilterSelection: updateTimerFormSelection,
  } = useTimer();

  const [searchValue, setSearchValue] = useState<string>('');
  const isTimerForm = isFromTimer ?? location?.state?.isFromTimer ?? false;
  const storedFilterSelection = isTimerForm ? timerFormSelection : transactionFormSelection;
  const updateStoredFilterSelection = isTimerForm
    ? updateTimerFormSelection
    : updateTransactionFormSelection;

  const onlyHourBasedTransactionTypes =
    onlyHourBased ??
    location?.state?.onlyHourBasedTransactionTypes ??
    transaction?.onlyHourBasedTransactionTypes ??
    false;

  const matterType = transaction?.matter?.invoiceableType ?? timer?.matter?.invoiceableType;
  const isInternalMatter = transaction?.matter?.invoiceableType === InvoiceableType.internal;
  const needsLinkedTypesLookup =
    Boolean(transaction?.matter?.id) &&
    transaction?.matter?.hasLinkedTransactionTypes === undefined;
  const matterId = needsLinkedTypesLookup ? transaction?.matter?.id : undefined;

  const { hasCategories, isLoading: isCheckingCategories } = useHasTransactionTypeCategories();
  const { data: matterWithLinkedTypes } = useFetchMatterById({
    id: matterId,
    withHasLinkedTransactionTypes: true,
  });
  const hasLinkedTransactionTypes =
    transaction?.matter?.hasLinkedTransactionTypes ??
    matterWithLinkedTypes?.hasLinkedTransactionTypes;
  const getInitialFilter = (): IFilterOption => {
    if (hasLinkedTransactionTypes) {
      return {
        label: t('transaction:transactionType.filter.matterTransactionTypes'),
        value: TransactionTypesValue.matterTransactionTypes,
      };
    }
    if (matterType === InvoiceableType.billable || matterType === InvoiceableType.nonBillable) {
      return {
        label: t('transaction:transactionType.filter.billable'),
        value: TransactionTypesValue.billableMatter,
      };
    }

    if (matterType === InvoiceableType.internal) {
      return {
        label: t('transaction:transactionType.filter.internal'),
        value: TransactionTypesValue.internalMatter,
      };
    }

    return {
      label: t('transaction:transactionType.filter.all'),
      value: TransactionTypesValue.all,
    };
  };

  // The user's own choices outlive this screen, but only for the matter they were made on: the
  // defaults, and which filters are locked, depend on the matter. hasLinkedTransactionTypes comes
  // from an async lookup, so until the user picks a filter the default is worked out afresh each
  // render and follows the lookup.
  const formMatterId = transaction?.matter?.id ?? timer?.matter?.id;
  const filterSelection = getSelectionForMatter(storedFilterSelection, formMatterId);
  const selectedFilter = filterSelection?.radioFilter ?? getInitialFilter();
  const selectedCategoryFilters = filterSelection?.categoryFilters ?? NO_CATEGORY_FILTERS;
  const updateFilterSelection = (
    change: (current: ITransactionTypeFilterSelection) => Partial<ITransactionTypeFilterSelection>,
  ) =>
    updateStoredFilterSelection((stored) => {
      const current: ITransactionTypeFilterSelection = getSelectionForMatter(
        stored,
        formMatterId,
      ) ?? { matterId: formMatterId, radioFilter: null, categoryFilters: NO_CATEGORY_FILTERS };
      return { ...current, ...change(current) };
    });

  const isMatterTransactionTypesFilterActive =
    hasLinkedTransactionTypes &&
    selectedFilter?.value === TransactionTypesValue.matterTransactionTypes;
  const disableFilter = isTransactionTypesFilterLocked(
    hasLinkedTransactionTypes,
    matterType,
    selectedFilter?.value,
  );

  let filterDisabledInfoMessage: string | undefined;
  if (disableFilter) {
    filterDisabledInfoMessage =
      selectedFilter?.value === TransactionTypesValue.internalMatter
        ? t('transaction:internalFilterLockedMessage')
        : t('transaction:billableFilterLockedMessage');
  }

  const transactionTypeSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'name',
        label: t('transaction:transactionType.transactionType'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });

  const {
    data: transactionTypeList,
    isLoading: isFirstPageLoading,
    error: transactionTypeError,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchTransactionTypesList({
    sortBy: ['createdOn:DESC'],
    relations: isMatterTransactionTypesFilterActive
      ? ['transactionType', 'transactionType.unit', 'transactionType.category']
      : ['category', 'unit'],
    pageSize: 100,
    search: searchValue,
    ...(isMatterTransactionTypesFilterActive
      ? { hasLinkedTransactionTypes: true, matterId: transaction?.matter?.id }
      : {}),
    extraFilters: {
      ...(!selectedFilter.value.includes(TransactionTypesValue.all) &&
      !selectedFilter.value.includes(TransactionTypesValue.matterTransactionTypes)
        ? { transactionTypeUsage: `$eq:${selectedFilter.value}` }
        : {}),
      ...(onlyHourBasedTransactionTypes &&
        !isMatterTransactionTypesFilterActive && { unitId: SystemConstants.UNIT_HOUR }), // restrict to hour based only when not querying the matter's own linked transaction types
    },
    ...(!isMatterTransactionTypesFilterActive && {
      fields: [
        'unitPrice',
        'unitId',
        'standardPrice',
        'units',
        'translations',
        'transactionTypeUsage',
        'createdOn',
      ],
    }),
  });

  const refreshTransactionTypeList = () => {
    queryClient.removeQueries({ queryKey: ['transaction-types-list'] });
    return refetch();
  };

  const sortedTransactionTypeData = useMemo(() => {
    const sorted = [...transactionTypeList].sort((a, b) => {
      const aTitle = getTranslationFromTranslatableEntity(a.translations, getAppLanguageCode());
      const bTitle = getTranslationFromTranslatableEntity(b.translations, getAppLanguageCode());
      return activeSort.direction === 'ASC'
        ? aTitle.localeCompare(bTitle)
        : bTitle.localeCompare(aTitle);
    });
    return sorted;
  }, [transactionTypeList, activeSort.direction]);

  const getMatterInvoiceableTypeFilter = (): IFilterOption =>
    isInternalMatter
      ? {
          label: t('transaction:transactionType.filter.internal'),
          value: TransactionTypesValue.internalMatter,
        }
      : {
          label: t('transaction:transactionType.filter.billable'),
          value: TransactionTypesValue.billableMatter,
        };

  const transactionTypesFilterOptions = getTransactionTypesFilterOptions(
    t,
    hasLinkedTransactionTypes,
  );
  const disabledTransactionTypesFilterValues = getDisabledTransactionTypesFilterValues(
    hasLinkedTransactionTypes,
    getMatterInvoiceableTypeFilter(),
  );

  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  const isLoadingEveryPage =
    hasCategories && !transactionTypeError && Boolean(hasNextPage || isFetchingNextPage);
  const isTransactionTypeLoading = isFirstPageLoading || isCheckingCategories || isLoadingEveryPage;

  useEffect(() => {
    if (
      hasCategories ||
      !inView ||
      !hasNextPage ||
      isFetchingNextPage ||
      !transactionTypeList?.length
    ) {
      return;
    }
    fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage]);

  // Categories group the whole list, so every page has to be in before anything renders.
  useEffect(() => {
    if (!hasCategories || transactionTypeError || !hasNextPage || isFetchingNextPage) {
      return;
    }
    fetchNextPage();
  }, [hasCategories, transactionTypeError, hasNextPage, isFetchingNextPage]);

  const formatedTransactionType: ITransactionTypes[] = useMemo(() => {
    if (isFirstPageLoading) {
      return [];
    }
    return isMatterTransactionTypesFilterActive
      ? sortedTransactionTypeData.map(
          (type: IMatterTransactionTypesResponse) => type.transactionType,
        )
      : sortedTransactionTypeData;
  }, [isFirstPageLoading, isMatterTransactionTypesFilterActive, sortedTransactionTypeData]);

  const noCategoryLabel = t('transaction:transactionType.noCategory');
  // The options come from every loaded type, so they stay put while categories are being ticked.
  const categoryFilterOptions: IFilterOption[] = useMemo(
    () =>
      hasCategories && !isTransactionTypeLoading
        ? buildCategoryFilterOptions(formatedTransactionType, appLanguage, noCategoryLabel)
        : [],
    [
      hasCategories,
      isTransactionTypeLoading,
      formatedTransactionType,
      appLanguage,
      noCategoryLabel,
    ],
  );
  const transactionTypeGroups: ITransactionTypeGroup[] = useMemo(() => {
    if (isTransactionTypeLoading || formatedTransactionType.length === 0) {
      return [];
    }
    if (!hasCategories) {
      return [
        {
          id: NO_CATEGORY_GROUP_ID,
          name: noCategoryLabel,
          transactionTypes: formatedTransactionType,
        },
      ];
    }
    const visibleTransactionTypes = filterTransactionTypesByCategory(
      formatedTransactionType,
      selectedCategoryFilters.map(({ value }) => value),
    );
    return groupTransactionTypesByCategory(visibleTransactionTypes, appLanguage, noCategoryLabel);
  }, [
    isTransactionTypeLoading,
    hasCategories,
    formatedTransactionType,
    selectedCategoryFilters,
    appLanguage,
    noCategoryLabel,
  ]);

  const setSelectedCategoryFilters = (categoryFilters: IFilterOption[]) =>
    updateFilterSelection(() => ({ categoryFilters }));
  const removeCategoryFilter = (value: string) =>
    updateFilterSelection(({ categoryFilters }) => ({
      categoryFilters: categoryFilters.filter((option) => option.value !== value),
    }));
  const clearCategoryFilters = () => updateFilterSelection(() => ({ categoryFilters: [] }));

  const handleSetSelectedFilter = (option: IFilterOption) =>
    updateFilterSelection(() => ({ radioFilter: option }));
  const resetFilterToDefault = () => updateFilterSelection(() => ({ radioFilter: null }));
  const resetFilterToMatterType = () =>
    updateFilterSelection(() => ({ radioFilter: getMatterInvoiceableTypeFilter() }));

  return {
    setSearchValue,
    selectedFilter,
    setSelectedFilter: handleSetSelectedFilter,
    transactionTypesFilterOptions,
    disabledTransactionTypesFilterValues,
    disableFilter,
    filterDisabledInfoMessage,
    isMatterTransactionTypesFilterActive,
    isInternalMatter,
    resetFilterToDefault,
    resetFilterToMatterType,
    transactionTypeSortOptions,
    activeSort,
    setActiveSort,
    formatedTransactionType,
    transactionTypeGroups,
    categoryFilterOptions,
    selectedCategoryFilters,
    setSelectedCategoryFilters,
    removeCategoryFilter,
    clearCategoryFilters,
    isTransactionTypeLoading,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage: hasNextPage && !hasCategories,
    ref,
    onlyHourBasedTransactionTypes,
    refreshTransactionTypeList,
  };
};
